import React, { useEffect, useMemo, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { ColorTab } from "@/components/ui/color-tab";
import {
  Smartphone, Search, Image as ImageIcon, Upload, X, Star, Percent, Weight,
  Package, Eye, EyeOff, RefreshCw, MoveLeft, MoveRight, Save, ExternalLink,
  Tags, Plus, Pencil, Trash2, Check, ShoppingBag, Truck, PackageCheck, Clock, MapPin, Phone,
} from "lucide-react";
import { inr, fmtDate } from "@/lib/utils-format";

/** Compress a File to a base64 JPEG (max 1200px on the longer edge, quality 0.8). */
async function compressToDataUrl(file, maxDim = 1200, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => { img.src = reader.result; };
    reader.onerror = reject;
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxDim / Math.max(width, height));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function MobileStorePage() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("");
  const [visibleOnly, setVisibleOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [countRows, setCountRows] = useState([]); // full unfiltered set used only for chip counts
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  // Allowlist of mobile-approved categories (Feb 2026 — locked to 6)
  const [mobileAllowedCats, setMobileAllowedCats] = useState([]);

  const loadCategories = async () => {
    try {
      const r = await api.get("/mobile-store/categories");
      setCatalog(r.data);
    } catch (e) { /* silent */ }
    try {
      const r2 = await api.get("/mobile-store/mobile-allowed-categories");
      setMobileAllowedCats(r2.data?.categories || []);
    } catch (e) { /* silent */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (category) params.set("category", category);
      if (visibleOnly) params.set("visible_only", "true");
      const r = await api.get(`/mobile-store${params.toString() ? `?${params.toString()}` : ""}`);
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [category, visibleOnly]);

  // Load recommended + existing category list once, and the full row set for chip counts
  useEffect(() => { loadCategories(); }, []);
  // Chip counts are ALWAYS derived from the full product list (respecting visible_only + search
  // but NOT category — otherwise counts collapse when a chip is active).
  useEffect(() => {
    const params = new URLSearchParams();
    if (search.trim()) params.set("search", search.trim());
    if (visibleOnly) params.set("visible_only", "true");
    api.get(`/mobile-store${params.toString() ? `?${params.toString()}` : ""}`)
      .then((r) => setCountRows(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [visibleOnly]);

  const doSearch = (e) => { e?.preventDefault?.(); load(); };

  const categories = useMemo(() => {
    // Prefer the API's ordered list (defaults first, then existing extras) — fallback to row-derived
    if (catalog.length) return catalog;
    return [...new Set(rows.map((r) => r.category).filter(Boolean))].sort();
  }, [catalog, rows]);

  const stats = useMemo(() => ({
    total: rows.length,
    visible: rows.filter((r) => r.mobile_visible).length,
    withImages: rows.filter((r) => (r.image_urls || []).length > 0).length,
    withDiscount: rows.filter((r) => r.retail_discount_pct > 0).length,
  }), [rows]);

  // Compute counts per category for the chip strip. Uses `rows` — reflects
  // whatever the current search + visible-only filter produces (excluding
  // category itself, which is what a chip toggles).
  const chipData = useMemo(() => {
    const src = countRows.length ? countRows : rows;
    const counts = new Map();
    for (const r of src) {
      const c = r.category || "Uncategorized";
      counts.set(c, (counts.get(c) || 0) + 1);
    }
    const ordered = [];
    for (const c of categories) {
      if (counts.has(c)) ordered.push({ name: c, count: counts.get(c) });
    }
    for (const [c, count] of counts) {
      const has = ordered.find((x) => x.name.toLowerCase() === (c || "").toLowerCase());
      if (!has) ordered.push({ name: c, count });
    }
    return ordered;
  }, [countRows, rows, categories]);

  const toggleVisible = async (r) => {
    try {
      await api.patch(`/mobile-store/${r.id}`, { mobile_visible: !r.mobile_visible });
      toast.success(r.mobile_visible ? "Hidden from mobile store" : "Now live on mobile store");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const moveToMobile = async (r, targetCategory) => {
    try {
      const resp = await api.post(`/mobile-store/${r.id}/move-to-mobile`, {
        category: targetCategory, make_visible: true,
      });
      toast.success(`Moved to ${resp.data?.category} · now live on mobile store`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const publicUrl = `${window.location.origin}/deals`;

  return (
    <div className="space-y-6" data-testid="mobile-store-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="uc-label">E-commerce · Mobile Storefront</div>
          <h1 className="font-[Outfit] text-3xl sm:text-4xl font-semibold tracking-tight mt-1">Mobile Store</h1>
          <p className="text-sm text-[#8B7355] mt-1">Curate what shows on <b>bakemill-mobile</b>. Photos, pricing, weight, discounts — updates go live instantly via <code className="text-[#C05621]">/api/mapi/catalog</code>.</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={publicUrl} target="_blank" rel="noreferrer" className="text-xs text-[#C05621] hover:underline flex items-center gap-1"><ExternalLink className="w-3 h-3" /> Preview public deals</a>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ms-refresh">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      <Tabs defaultValue="catalog">
        <TabsList className="bg-transparent p-0 gap-2 h-auto flex-wrap">
          <ColorTab value="catalog" tone="orange" icon={Package} testid="ms-tab-catalog">Product Catalog</ColorTab>
          <ColorTab value="orders" tone="blue" icon={ShoppingBag} testid="ms-tab-orders">Mobile Orders</ColorTab>
        </TabsList>

        <TabsContent value="catalog" className="pt-4 space-y-6">
      {/* Stat tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile label="Total Products" value={stats.total} tone="neutral" icon={Package} />
        <StatTile label="Live on Mobile" value={stats.visible} tone="emerald" icon={Smartphone} />
        <StatTile label="With Photos" value={stats.withImages} tone="blue" icon={ImageIcon} />
        <StatTile label="With Discount" value={stats.withDiscount} tone="amber" icon={Percent} />
      </div>

      {/* Category chip strip — one-click filter */}
      <div className="bg-white border border-[#EAE5D9] rounded-md p-3" data-testid="ms-chip-strip">
        <div className="flex items-center justify-between mb-2">
          <div className="uc-label !text-[#5C4033]">Filter by category</div>
          <Button
            variant="outline" size="sm" onClick={() => setManageCatsOpen(true)}
            data-testid="ms-manage-categories-btn"
            className="h-7 text-xs"
          >
            <Tags className="w-3 h-3 mr-1" /> Manage Categories
          </Button>
        </div>
        <div className="flex flex-wrap gap-1.5">
          <CategoryChip label="All" count={countRows.length || rows.length} active={!category} onClick={() => setCategory("")} testid="ms-chip-all" />
          {chipData.map((c) => (
            <CategoryChip key={c.name} label={c.name} count={c.count}
              active={category === c.name}
              onClick={() => setCategory(category === c.name ? "" : c.name)}
              testid={`ms-chip-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
            />
          ))}
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={doSearch} className="flex flex-wrap items-end gap-2 p-3 bg-white border border-[#EAE5D9] rounded-md">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-[10px] uppercase text-[#8B7355]">Search</Label>
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Product name or SKU…" className="pl-7" data-testid="ms-search" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase text-[#8B7355]">Category</Label>
          <Select value={category} onValueChange={(v) => setCategory(v === "__all" ? "" : v)}>
            <SelectTrigger className="w-44" data-testid="ms-category"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="__all">All</SelectItem>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <label className="flex items-center gap-2 text-xs cursor-pointer px-2 py-2">
          <Switch checked={visibleOnly} onCheckedChange={setVisibleOnly} data-testid="ms-visible-only" />
          <span>Live on mobile only</span>
        </label>
        <Button type="submit" variant="outline" size="sm"><Search className="w-3.5 h-3.5 mr-1" /> Search</Button>
      </form>

      {/* Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="ms-grid">
        {rows.map((r) => (
          <ProductCard key={r.id} r={r}
            allowedCats={mobileAllowedCats}
            onEdit={() => setEditing(r)}
            onToggle={() => toggleVisible(r)}
            onMove={(cat) => moveToMobile(r, cat)}
          />
        ))}
        {rows.length === 0 && !loading && (
          <div className="col-span-full text-center py-16 bg-white border border-dashed border-[#EAE5D9] rounded-lg">
            <Package className="w-10 h-10 mx-auto text-[#8B7355] opacity-40 mb-2" />
            <div className="text-sm text-[#8B7355]">No products match your filters.</div>
          </div>
        )}
      </div>
        </TabsContent>

        <TabsContent value="orders" className="pt-4">
          <MobileOrdersPanel />
        </TabsContent>
      </Tabs>

      {editing && <EditDialog product={editing} categories={categories} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {manageCatsOpen && (
        <ManageCategoriesDialog
          onClose={() => setManageCatsOpen(false)}
          onChanged={() => { loadCategories(); load(); }}
        />
      )}
    </div>
  );
}

/* ============================================================
 *  MOBILE ORDERS PANEL — list, dispatch, receive, cancel
 * ============================================================ */
const STATUS_TONE = {
  pending: "bg-amber-100 text-amber-800 border-amber-200",
  dispatched: "bg-blue-100 text-blue-800 border-blue-200",
  received: "bg-emerald-100 text-emerald-800 border-emerald-200",
  cancelled: "bg-slate-100 text-slate-700 border-slate-200",
};

function MobileOrdersPanel() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [dispatchFor, setDispatchFor] = useState(null);
  const [receiveFor, setReceiveFor] = useState(null);
  const [cancelFor, setCancelFor] = useState(null);
  const [detailsFor, setDetailsFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [s, o] = await Promise.all([
        api.get("/mobile-store/orders/summary"),
        api.get("/mobile-store/orders", { params: { status: statusFilter === "all" ? undefined : statusFilter, q: q || undefined } }),
      ]);
      setSummary(s.data); setOrders(o.data);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  return (
    <div className="space-y-4" data-testid="ms-orders-panel">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <SummaryTile label="Pending" value={summary.counts.pending} tone="amber" icon={Clock} onClick={() => setStatusFilter("pending")} active={statusFilter === "pending"} testid="ms-orders-tile-pending" />
          <SummaryTile label="Dispatched" value={summary.counts.dispatched} tone="blue" icon={Truck} onClick={() => setStatusFilter("dispatched")} active={statusFilter === "dispatched"} testid="ms-orders-tile-dispatched" />
          <SummaryTile label="Received" value={summary.counts.received} tone="emerald" icon={PackageCheck} onClick={() => setStatusFilter("received")} active={statusFilter === "received"} testid="ms-orders-tile-received" />
          <SummaryTile label="Cancelled" value={summary.counts.cancelled} tone="slate" icon={X} onClick={() => setStatusFilter("cancelled")} active={statusFilter === "cancelled"} testid="ms-orders-tile-cancelled" />
          <SummaryTile label={`Today · ${summary.today.orders} orders`} value={inr(summary.today.revenue)} tone="orange" icon={ShoppingBag} testid="ms-orders-tile-today" />
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
          <Input placeholder="Search order # / customer / phone…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="pl-7 bg-white" data-testid="ms-orders-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 bg-white" data-testid="ms-orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="received">Received</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
      </div>

      <div className="bg-white border border-[#EAE5D9] rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FAF6EE] text-xs uppercase tracking-wider text-[#8B7355]">
            <tr>
              <th className="text-left px-4 py-2.5">Order #</th>
              <th className="text-left px-4 py-2.5">Date</th>
              <th className="text-left px-4 py-2.5">Customer</th>
              <th className="text-left px-4 py-2.5">Product Ordered</th>
              <th className="text-left px-4 py-2.5">Delivery Address</th>
              <th className="text-right px-4 py-2.5">Total</th>
              <th className="text-left px-4 py-2.5">Status</th>
              <th className="text-right px-4 py-2.5">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="ms-orders-rows">
            {orders.map((o) => (
              <tr key={o.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7] align-top" data-testid={`ms-order-row-${o.id}`}>
                <td className="px-4 py-2.5 font-mono text-xs font-bold text-[#C05621]">{o.number}<div className="text-[10px] text-[#8B7355] font-sans">{o.fulfilment_type}</div></td>
                <td className="px-4 py-2.5 text-xs">{fmtDate(o.created_at)}<div className="text-[10px] text-[#8B7355]">{(o.created_at || "").slice(11, 16)}</div></td>
                <td className="px-4 py-2.5">
                  <div className="font-medium">{o.customer_name || "—"}</div>
                  {o.customer_phone && <div className="text-[10px] text-[#8B7355] flex items-center gap-1"><Phone className="w-2.5 h-2.5" /> {o.customer_phone}</div>}
                </td>
                <td className="px-4 py-2.5 text-xs max-w-[280px]">
                  <div className="line-clamp-2">{o.product_summary || "—"}</div>
                  {(o.items || []).length > 3 && <div className="text-[10px] text-[#8B7355]">+ {o.items.length - 3} more</div>}
                </td>
                <td className="px-4 py-2.5 text-xs max-w-[240px]">
                  {o.fulfilment_type === "delivery" ? (
                    <div className="flex items-start gap-1">
                      <MapPin className="w-3 h-3 text-[#8B7355] mt-0.5 flex-shrink-0" />
                      <span className="line-clamp-2">{o.delivery_address || "—"}{o.delivery_pincode && ` — ${o.delivery_pincode}`}</span>
                    </div>
                  ) : (
                    <Badge variant="outline" className="text-[10px] bg-[#FAF6EE]">Pickup @ {o.pickup_branch_name || "store"}</Badge>
                  )}
                </td>
                <td className="px-4 py-2.5 text-right font-mono font-semibold">{inr(o.grand_total)}</td>
                <td className="px-4 py-2.5">
                  <Badge variant="outline" className={`uppercase text-[10px] ${STATUS_TONE[o.status] || ""}`}>{o.status}</Badge>
                  {o.status === "dispatched" && o.dispatched_at && <div className="text-[10px] text-[#8B7355] mt-1">Dispatched {fmtDate(o.dispatched_at)}</div>}
                  {o.status === "received" && o.received_at && <div className="text-[10px] text-emerald-700 mt-1">Received {fmtDate(o.received_at)}</div>}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap space-x-1">
                  {o.status === "pending" && (
                    <>
                      <Button size="sm" className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => setDispatchFor(o)} data-testid={`ms-order-dispatch-${o.id}`}>
                        <Truck className="w-3 h-3 mr-1" /> Mark Dispatched
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setCancelFor(o)} data-testid={`ms-order-cancel-${o.id}`}>
                        <X className="w-3 h-3" />
                      </Button>
                    </>
                  )}
                  {o.status === "dispatched" && (
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => setReceiveFor(o)} data-testid={`ms-order-receive-${o.id}`}>
                      <PackageCheck className="w-3 h-3 mr-1" /> Mark Received
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setDetailsFor(o)} data-testid={`ms-order-view-${o.id}`}>View</Button>
                </td>
              </tr>
            ))}
            {orders.length === 0 && !loading && (
              <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]"><ShoppingBag className="w-8 h-8 mx-auto mb-2 opacity-40" />No mobile orders in this filter</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {dispatchFor && <DispatchDialog order={dispatchFor} onClose={() => setDispatchFor(null)} onSaved={() => { setDispatchFor(null); load(); }} />}
      {receiveFor && <ReceiveDialog order={receiveFor} onClose={() => setReceiveFor(null)} onSaved={() => { setReceiveFor(null); load(); }} />}
      {cancelFor && <CancelDialog order={cancelFor} onClose={() => setCancelFor(null)} onSaved={() => { setCancelFor(null); load(); }} />}
      {detailsFor && <OrderDetailsDialog order={detailsFor} onClose={() => setDetailsFor(null)} />}
    </div>
  );
}

function SummaryTile({ label, value, tone, icon: Icon, onClick, active, testid }) {
  const map = {
    amber: "bg-amber-50 border-amber-200 text-amber-800",
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    slate: "bg-slate-50 border-slate-200 text-slate-700",
    orange: "bg-[#FEF9EA] border-[#C05621] text-[#C05621]",
  };
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      data-testid={testid}
      className={`rounded-lg border p-3 text-left transition ${map[tone] || ""} ${active ? "ring-2 ring-offset-2 ring-[#C05621]" : ""} ${onClick ? "cursor-pointer hover:brightness-95" : "cursor-default"}`}
    >
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider opacity-80">{Icon && <Icon className="w-3.5 h-3.5" />} {label}</div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </button>
  );
}

function DispatchDialog({ order, onClose, onSaved }) {
  const [form, setForm] = useState({ courier: "", tracking_number: "", rider_name: "", rider_phone: "", eta: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/mobile-store/orders/${order.id}/dispatch`, form);
      toast.success(`Order ${order.number} marked as dispatched`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="ms-dispatch-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Mark Dispatched — {order.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Records dispatch time and rider/courier details. Customer will see status "Dispatched" on the mobile app.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Courier / Service</Label><Input value={form.courier} onChange={(e) => setForm({ ...form, courier: e.target.value })} placeholder="Dunzo / Own rider" data-testid="ms-dispatch-courier" /></div>
          <div><Label>Tracking #</Label><Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })} /></div>
          <div><Label>Rider Name</Label><Input value={form.rider_name} onChange={(e) => setForm({ ...form, rider_name: e.target.value })} data-testid="ms-dispatch-rider" /></div>
          <div><Label>Rider Phone</Label><Input value={form.rider_phone} onChange={(e) => setForm({ ...form, rider_phone: e.target.value })} data-testid="ms-dispatch-phone" /></div>
          <div className="col-span-2"><Label>ETA</Label><Input value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} placeholder="e.g. 45 minutes" /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="ms-dispatch-save">
            <Truck className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Confirm Dispatch"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReceiveDialog({ order, onClose, onSaved }) {
  const [form, setForm] = useState({ received_by: order.customer_name || "", notes: "" });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/mobile-store/orders/${order.id}/receive`, form);
      toast.success(`Order ${order.number} marked as received`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="ms-receive-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Mark Received — {order.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Confirms customer handover. Records receiver name + optional notes/proof.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Received By</Label><Input value={form.received_by} onChange={(e) => setForm({ ...form, received_by: e.target.value })} placeholder="Customer name / relative" data-testid="ms-receive-by" /></div>
          <div><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Handed at door / OTP verified / etc." /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="ms-receive-save">
            <PackageCheck className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Confirm Received"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelDialog({ order, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!reason.trim()) return toast.error("Reason is required");
    setSaving(true);
    try {
      await api.post(`/mobile-store/orders/${order.id}/cancel`, { reason: reason.trim() });
      toast.success(`Order ${order.number} cancelled`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-md" data-testid="ms-cancel-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Cancel Order — {order.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Cancellation is recorded to the audit log and the customer sees the reason in their app.</DialogDescription>
        </DialogHeader>
        <div><Label>Reason *</Label><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Out of stock / customer requested" data-testid="ms-cancel-reason" /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Back</Button>
          <Button variant="destructive" onClick={save} disabled={saving} data-testid="ms-cancel-save">{saving ? "Cancelling…" : "Cancel Order"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OrderDetailsDialog({ order, onClose }) {
  const d = order.dispatch_details || {};
  const r = order.receive_details || {};
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Order {order.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Placed on {fmtDate(order.created_at)} by {order.customer_name} · {order.customer_phone}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={`uppercase ${STATUS_TONE[order.status] || ""}`}>{order.status}</Badge>
            <Badge variant="outline">{order.fulfilment_type}</Badge>
            <Badge variant="outline">{order.payment_method} · {order.payment_status}</Badge>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div><div className="text-[10px] uppercase text-[#8B7355]">Delivery Address</div><div>{order.delivery_address || "—"}</div>{order.delivery_pincode && <div className="text-xs text-[#8B7355]">Pincode: {order.delivery_pincode}</div>}</div>
            <div><div className="text-[10px] uppercase text-[#8B7355]">Delivery Slot</div><div>{order.delivery_slot || "—"}</div></div>
          </div>
          <div className="border border-[#EAE5D9] rounded overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-[#FAF6EE] uppercase text-[#8B7355]"><tr><th className="text-left px-3 py-1.5">Product</th><th className="text-right px-3 py-1.5">Qty</th><th className="text-right px-3 py-1.5">Rate</th><th className="text-right px-3 py-1.5">Total</th></tr></thead>
              <tbody>
                {(order.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-[#EAE5D9]"><td className="px-3 py-1.5">{it.name}</td><td className="px-3 py-1.5 text-right">{it.quantity}</td><td className="px-3 py-1.5 text-right">{inr(it.unit_price)}</td><td className="px-3 py-1.5 text-right font-mono">{inr(it.line_total)}</td></tr>
                ))}
                <tr className="border-t-2 border-[#2C1810] bg-[#FEF9EA]"><td colSpan={3} className="px-3 py-1.5 text-right font-bold">Grand Total</td><td className="px-3 py-1.5 text-right font-bold text-[#C05621]">{inr(order.grand_total)}</td></tr>
              </tbody>
            </table>
          </div>
          {d.dispatched_at && (
            <div className="rounded border border-blue-200 bg-blue-50 p-3 text-xs">
              <div className="font-semibold text-blue-800 flex items-center gap-1"><Truck className="w-3.5 h-3.5" /> Dispatched at {fmtDate(d.dispatched_at)} {(d.dispatched_at || "").slice(11, 16)}</div>
              <div className="text-blue-900 mt-1">Courier: {d.courier || "—"} · Rider: {d.rider_name || "—"} {d.rider_phone && `(${d.rider_phone})`} · Tracking: {d.tracking_number || "—"} · ETA: {d.eta || "—"}</div>
              {d.notes && <div className="italic mt-1">"{d.notes}"</div>}
              <div className="text-[10px] mt-1 text-[#8B7355]">By {d.dispatched_by_name || "system"}</div>
            </div>
          )}
          {r.received_at && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-xs">
              <div className="font-semibold text-emerald-800 flex items-center gap-1"><PackageCheck className="w-3.5 h-3.5" /> Received at {fmtDate(r.received_at)} {(r.received_at || "").slice(11, 16)}</div>
              <div className="text-emerald-900 mt-1">By: {r.received_by || "—"} · Confirmed by: {r.confirmed_by_name || "—"}</div>
              {r.notes && <div className="italic mt-1">"{r.notes}"</div>}
            </div>
          )}
          {order.cancel_reason && (
            <div className="rounded border border-red-200 bg-red-50 p-3 text-xs">
              <div className="font-semibold text-red-800">Cancelled: {order.cancel_reason}</div>
              <div className="text-[10px] mt-1 text-[#8B7355]">On {fmtDate(order.cancelled_at)}</div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function StatTile({ label, value, tone, icon: Icon }) {
  const bg = { emerald: "bg-emerald-50 border-emerald-200 text-emerald-800", blue: "bg-blue-50 border-blue-200 text-blue-800", amber: "bg-amber-50 border-amber-200 text-amber-800", neutral: "bg-white border-[#EAE5D9]" }[tone];
  return (
    <div className={`border rounded-lg p-3 ${bg}`}>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">{Icon && <Icon className="w-3.5 h-3.5" />} {label}</div>
      <div className="text-2xl font-[Outfit] font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function CategoryChip({ label, count, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
        active
          ? "bg-[#C05621] text-white border-[#C05621] shadow-sm"
          : "bg-white text-[#5C4033] border-[#EAE5D9] hover:border-[#C05621] hover:bg-[#FEF9EA]"
      }`}
    >
      {label}
      <span className={`ml-1.5 text-[10px] ${active ? "opacity-80" : "text-[#8B7355]"}`}>
        {count}
      </span>
    </button>
  );
}

function ProductCard({ r, onEdit, onToggle, onMove, allowedCats = [] }) {
  const disc = r.retail_discount_pct || 0;
  const base = Number(r.sale_price || r.mrp || 0);
  const dealPrice = disc > 0 ? Math.round(base * (100 - disc) / 100.0 * 100) / 100 : base;
  const hasPhoto = (r.image_urls || []).length > 0;
  const inAllowlist = allowedCats.some(
    (c) => (c || "").toLowerCase() === (r.category || "").toLowerCase()
  );
  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${r.mobile_visible ? "border-[#EAE5D9]" : "border-slate-200 opacity-80"}`} data-testid={`ms-card-${r.id}`}>
      <div className="relative">
        {hasPhoto ? (
          <img src={r.image_urls[0]} alt={r.name} className="w-full h-40 object-cover" />
        ) : (
          <div className="w-full h-40 bg-gradient-to-br from-[#FEF9EA] to-[#FEEBC8] flex items-center justify-center text-4xl">🍰</div>
        )}
        {disc > 0 && (
          <div className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-orange-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
            {Math.round(disc)}% OFF
          </div>
        )}
        {r.mobile_visible ? (
          <Badge className="absolute top-2 right-2 border-0 bg-emerald-500 text-white"><Eye className="w-3 h-3 mr-0.5" /> Live</Badge>
        ) : (
          <Badge className="absolute top-2 right-2 border-0 bg-slate-500 text-white"><EyeOff className="w-3 h-3 mr-0.5" /> Hidden</Badge>
        )}
        {r.image_urls?.length > 1 && (
          <Badge className="absolute bottom-2 right-2 border-0 bg-black/50 text-white text-[10px]">+{r.image_urls.length - 1} more</Badge>
        )}
      </div>
      <div className="p-3">
        <div className="font-medium truncate">{r.name}</div>
        <div className="text-[10px] text-[#8B7355] flex items-center gap-2 mt-0.5">
          <span>{r.sku}</span>
          {r.category && <span>· {r.category}</span>}
        </div>
        <div className="flex items-end justify-between mt-2">
          <div>
            <div className="text-lg font-[Outfit] font-semibold text-[#C05621]">{inr(dealPrice)}</div>
            {disc > 0 && <div className="text-[10px] text-[#8B7355] line-through">{inr(base)}</div>}
          </div>
          <div className="text-right text-[10px] text-[#5C4033]">
            {r.weight && <div className="flex items-center gap-0.5 justify-end"><Weight className="w-3 h-3" /> {r.weight}</div>}
            <div>Stock: {r.current_stock || 0} {r.unit}</div>
          </div>
        </div>
        <div className="flex gap-1.5 mt-3">
          <Button size="sm" variant="outline" className="flex-1" onClick={onEdit} data-testid={`ms-edit-${r.id}`}>Edit</Button>
          <Button size="sm" variant={r.mobile_visible ? "outline" : "default"} className={r.mobile_visible ? "" : "bg-emerald-600 text-white"} onClick={onToggle} data-testid={`ms-toggle-${r.id}`}>
            {r.mobile_visible ? <><EyeOff className="w-3 h-3 mr-1" /> Hide</> : <><Eye className="w-3 h-3 mr-1" /> Go Live</>}
          </Button>
        </div>
        {allowedCats.length > 0 && (
          <div className="mt-2">
            <Select value="" onValueChange={(v) => v && onMove?.(v)}>
              <SelectTrigger
                className={`h-8 text-xs ${inAllowlist ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`}
                data-testid={`ms-move-${r.id}`}
              >
                <MoveRight className="w-3 h-3 mr-1 text-[#8B7355]" />
                <SelectValue placeholder={inAllowlist ? "Move to another mobile category…" : "Move to Mobile category…"} />
              </SelectTrigger>
              <SelectContent className="bg-white">
                {allowedCats.map((c) => (
                  <SelectItem key={c} value={c} disabled={c.toLowerCase() === (r.category || "").toLowerCase()}
                    data-testid={`ms-move-${r.id}-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                    {c}{c.toLowerCase() === (r.category || "").toLowerCase() ? " · current" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!inAllowlist && (
              <div className="text-[10px] text-amber-700 mt-1">Currently in <b>{r.category || "Uncategorized"}</b> — not visible on the mobile app.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== Edit Dialog ====================
function EditDialog({ product: initial, categories = [], onClose, onSaved }) {
  const [p, setP] = useState({ ...initial });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const set = (patch) => setP((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        name: p.name,
        category: p.category || null,
        description: p.description,
        mobile_short_description: p.mobile_short_description,
        weight: p.weight,
        unit: p.unit,
        mrp: Number(p.mrp) || 0,
        sale_price: Number(p.sale_price) || 0,
        retail_discount_pct: Number(p.retail_discount_pct) || 0,
        current_stock: Number(p.current_stock) || 0,
        mobile_visible: !!p.mobile_visible,
        mobile_order: Number(p.mobile_order) || 0,
      };
      await api.patch(`/mobile-store/${p.id}`, payload);
      toast.success("Saved · pushed to mobile store");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addImage = async (file) => {
    if (!file) return;
    if ((p.image_urls || []).length >= 5) { toast.error("Max 5 images — remove one first"); return; }
    setUploading(true);
    try {
      const dataUrl = await compressToDataUrl(file, 1200, 0.8);
      const r = await api.post(`/mobile-store/${p.id}/images`, { data_url: dataUrl });
      set({ image_urls: r.data.image_urls });
      toast.success(`Photo added (${r.data.count}/5)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const removeImage = async (idx) => {
    try {
      const r = await api.delete(`/mobile-store/${p.id}/images/${idx}`);
      set({ image_urls: r.data.image_urls });
      toast.success("Photo removed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const moveImage = async (idx, dir) => {
    const arr = [...(p.image_urls || [])];
    const target = idx + dir;
    if (target < 0 || target >= arr.length) return;
    [arr[idx], arr[target]] = [arr[target], arr[idx]];
    try {
      const r = await api.put(`/mobile-store/${p.id}/images`, { image_urls: arr });
      set({ image_urls: r.data.image_urls });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const base = Number(p.sale_price || p.mrp || 0);
  const disc = Number(p.retail_discount_pct || 0);
  const dealPrice = disc > 0 ? Math.round(base * (100 - disc) / 100.0 * 100) / 100 : base;

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-[Outfit]">Mobile Store — {p.name}</DialogTitle></DialogHeader>

        <div className="space-y-4 pt-2">
          {/* Live toggle */}
          <label className="flex items-center gap-3 bg-[#FEF9EA] border border-amber-200 rounded p-3 cursor-pointer">
            <Switch checked={!!p.mobile_visible} onCheckedChange={(v) => set({ mobile_visible: v })} data-testid="ms-form-visible" />
            <div className="flex-1">
              <div className="font-medium text-sm">Show this product on the mobile store</div>
              <div className="text-xs text-[#8B7355]">Turn off to instantly hide from bakemill-mobile without deleting the product.</div>
            </div>
            {p.mobile_visible ? (
              <Badge className="border-0 bg-emerald-500 text-white"><Eye className="w-3 h-3 mr-0.5" /> LIVE</Badge>
            ) : (
              <Badge className="border-0 bg-slate-400 text-white"><EyeOff className="w-3 h-3 mr-0.5" /> HIDDEN</Badge>
            )}
          </label>

          {/* Gallery */}
          <div>
            <div className="uc-label !text-[#5C4033] mb-2 flex items-center gap-2">
              <ImageIcon className="w-3 h-3" /> Photo Gallery ({(p.image_urls || []).length}/5)
              <span className="text-[10px] text-[#8B7355] normal-case">· first photo is the thumbnail</span>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
              {(p.image_urls || []).map((url, idx) => (
                <div key={idx} className="relative group aspect-square rounded overflow-hidden border border-[#EAE5D9]" data-testid={`ms-form-image-${idx}`}>
                  <img src={url} alt="" className="w-full h-full object-cover" />
                  {idx === 0 && <Badge className="absolute top-1 left-1 border-0 bg-amber-500 text-white text-[9px]"><Star className="w-2.5 h-2.5 mr-0.5" /> Primary</Badge>}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition">
                    {idx > 0 && <button onClick={() => moveImage(idx, -1)} className="p-1 bg-white/90 rounded" title="Move left" data-testid={`ms-form-image-left-${idx}`}><MoveLeft className="w-3 h-3" /></button>}
                    {idx < p.image_urls.length - 1 && <button onClick={() => moveImage(idx, +1)} className="p-1 bg-white/90 rounded" title="Move right" data-testid={`ms-form-image-right-${idx}`}><MoveRight className="w-3 h-3" /></button>}
                    <button onClick={() => removeImage(idx)} className="p-1 bg-red-500 text-white rounded" title="Remove" data-testid={`ms-form-image-remove-${idx}`}><X className="w-3 h-3" /></button>
                  </div>
                </div>
              ))}
              {(p.image_urls || []).length < 5 && (
                <label className="aspect-square rounded border-2 border-dashed border-[#EAE5D9] flex flex-col items-center justify-center cursor-pointer hover:border-[#C05621] hover:bg-[#FEF9EA] text-[#8B7355] hover:text-[#C05621]" data-testid="ms-form-image-add">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" disabled={uploading}
                    onChange={(e) => addImage(e.target.files?.[0])} />
                  {uploading ? (
                    <><RefreshCw className="w-6 h-6 animate-spin mb-1" /><span className="text-[10px]">Compressing…</span></>
                  ) : (
                    <><Upload className="w-6 h-6 mb-1" /><span className="text-[10px]">Add photo</span><span className="text-[9px]">(auto-compressed)</span></>
                  )}
                </label>
              )}
            </div>
          </div>

          {/* Fields */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Product name*</Label><Input value={p.name || ""} onChange={(e) => set({ name: e.target.value })} data-testid="ms-form-name" /></div>
            <div className="col-span-2">
              <Label>Category</Label>
              <div className="flex gap-2">
                <Select value={p.category || ""} onValueChange={(v) => set({ category: v === "__custom" ? "" : v })}>
                  <SelectTrigger className="flex-1" data-testid="ms-form-category-select">
                    <SelectValue placeholder="Pick a category (Cake, Muffins, Rusk, Cookies…)" />
                  </SelectTrigger>
                  <SelectContent className="bg-white max-h-64">
                    {categories.map((c) => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                    <SelectItem value="__custom">+ Custom (type below)</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  value={p.category || ""}
                  onChange={(e) => set({ category: e.target.value })}
                  placeholder="Or type new…"
                  className="flex-1"
                  data-testid="ms-form-category-custom"
                />
              </div>
              <div className="text-[10px] text-[#8B7355] mt-1">Category shows the product under the right tab on the mobile store. Pick from the list or type a new one.</div>
            </div>
            <div><Label>Unit</Label><Input value={p.unit || ""} onChange={(e) => set({ unit: e.target.value })} placeholder="pcs / kg" data-testid="ms-form-unit" /></div>
            <div><Label>Weight (mobile display)</Label><Input value={p.weight || ""} onChange={(e) => set({ weight: e.target.value })} placeholder="500 g / 1 kg / 6 pcs" data-testid="ms-form-weight" /></div>
            <div><Label>MRP (₹)</Label><Input type="number" min="0" step="1" value={p.mrp ?? 0} onChange={(e) => set({ mrp: e.target.value })} data-testid="ms-form-mrp" /></div>
            <div><Label>Sale price (₹)</Label><Input type="number" min="0" step="1" value={p.sale_price ?? 0} onChange={(e) => set({ sale_price: e.target.value })} data-testid="ms-form-sale" /></div>
            <div><Label>Retail discount %</Label>
              <Input type="number" min="0" max="90" step="1" value={p.retail_discount_pct ?? 0} onChange={(e) => set({ retail_discount_pct: e.target.value })} data-testid="ms-form-discount" />
              {disc > 0 && <div className="text-[10px] text-emerald-700 mt-0.5">Mobile shows <b>{inr(dealPrice)}</b> (was <span className="line-through">{inr(base)}</span>)</div>}
            </div>
            <div><Label>Current stock</Label><Input type="number" min="0" step="1" value={p.current_stock ?? 0} onChange={(e) => set({ current_stock: e.target.value })} data-testid="ms-form-stock" /></div>
            <div className="col-span-2"><Label>Short description (mobile thumbnail line)</Label>
              <Input value={p.mobile_short_description || ""} onChange={(e) => set({ mobile_short_description: e.target.value })} placeholder="e.g. Rich chocolate, baked fresh daily" data-testid="ms-form-short" />
            </div>
            <div className="col-span-2"><Label>Full description</Label>
              <Textarea rows={3} value={p.description || ""} onChange={(e) => set({ description: e.target.value })} placeholder="Ingredients, allergens, storage instructions…" data-testid="ms-form-desc" />
            </div>
            <div><Label>Display order (lower = earlier)</Label><Input type="number" step="1" value={p.mobile_order ?? 0} onChange={(e) => set({ mobile_order: e.target.value })} data-testid="ms-form-order" /></div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={save} disabled={busy} className="bg-[#2C1810] text-white" data-testid="ms-form-save">
            <Save className="w-3.5 h-3.5 mr-1" /> Save & push to mobile
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ==================== Manage Categories Dialog ====================
function ManageCategoriesDialog({ onClose, onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mobile-store/categories/detailed");
      setRows(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load categories"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const createCat = async () => {
    const name = newName.trim();
    if (!name) { toast.error("Enter a category name"); return; }
    setCreating(true);
    try {
      await api.post("/mobile-store/categories", { name });
      toast.success(`"${name}" added`);
      setNewName("");
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add"); }
    finally { setCreating(false); }
  };

  const startEdit = (row) => { setEditingId(row.id); setEditName(row.name); };
  const cancelEdit = () => { setEditingId(null); setEditName(""); };

  const saveEdit = async (row) => {
    const name = editName.trim();
    if (!name || name === row.name) { cancelEdit(); return; }
    try {
      await api.patch(`/mobile-store/categories/${row.id}`, { name });
      toast.success(`Renamed to "${name}"`);
      cancelEdit();
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rename failed"); }
  };

  const removeCat = async (row) => {
    const msg = row.product_count > 0
      ? `Delete "${row.name}"? ${row.product_count} product(s) will become Uncategorized until reassigned.`
      : `Delete "${row.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      const r = await api.delete(`/mobile-store/categories/${row.id}`);
      toast.success(
        r.data?.products_cleared
          ? `Deleted · ${r.data.products_cleared} product(s) uncategorized`
          : "Deleted"
      );
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleChannel = async (row, field) => {
    const nextVal = !row[field];
    try {
      await api.patch(`/mobile-store/categories/${row.id}`, { [field]: nextVal });
      toast.success(
        `"${row.name}" ${nextVal ? "shown on" : "hidden from"} ${field === "show_in_mobile" ? "Mobile app" : "POS"}`
      );
      await load();
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl" data-testid="ms-manage-cats-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Outfit] flex items-center gap-2">
            <Tags className="w-4 h-4" /> Manage Categories
          </DialogTitle>
          <DialogDescription className="text-[11px] text-[#8B7355]">
            Toggle each category&apos;s visibility in the Mobile app and the live POS independently. Renames and deletes
            propagate to <code className="text-[#C05621]">bakemill-mobile</code> and the POS product search instantly.
          </DialogDescription>
        </DialogHeader>

        {/* Add new */}
        <div className="flex gap-2 mt-3">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="New category name (e.g. Brownies)"
            onKeyDown={(e) => { if (e.key === "Enter") createCat(); }}
            data-testid="ms-cat-new-name"
          />
          <Button onClick={createCat} disabled={creating || !newName.trim()}
            className="bg-[#C05621] text-white hover:bg-[#a04519]" data-testid="ms-cat-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add
          </Button>
        </div>

        {/* List */}
        <div className="mt-4 border border-[#EAE5D9] rounded-md divide-y max-h-[420px] overflow-y-auto" data-testid="ms-cat-list">
          {/* Column header */}
          {!loading && rows.length > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-[#FEF9EA] text-[10px] uppercase tracking-wider text-[#8B7355]">
              <div className="flex-1">Category</div>
              <div className="w-24 text-center">Mobile app</div>
              <div className="w-20 text-center">POS</div>
              <div className="w-24 text-right">Actions</div>
            </div>
          )}
          {loading && <div className="p-4 text-center text-xs text-[#8B7355]">Loading…</div>}
          {!loading && rows.length === 0 && (
            <div className="p-6 text-center text-xs text-[#8B7355]">No categories yet — add your first above.</div>
          )}
          {rows.map((row) => (
            <div key={row.id} className="flex items-center gap-2 px-3 py-2" data-testid={`ms-cat-row-${row.id}`}>
              {editingId === row.id ? (
                <>
                  <Input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") saveEdit(row);
                      if (e.key === "Escape") cancelEdit();
                    }}
                    autoFocus
                    className="flex-1 h-8"
                    data-testid={`ms-cat-edit-input-${row.id}`}
                  />
                  <Button size="sm" variant="outline" onClick={() => saveEdit(row)}
                    className="h-8 px-2" data-testid={`ms-cat-save-${row.id}`}>
                    <Check className="w-3.5 h-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={cancelEdit} className="h-8 px-2">
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate" data-testid={`ms-cat-name-${row.id}`}>
                      {row.name}
                    </div>
                    <div className="text-[10px] text-[#8B7355]">
                      {row.product_count} product{row.product_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="w-24 flex justify-center" title={row.show_in_mobile !== false ? "Visible on mobile — click to hide" : "Hidden from mobile — click to show"}>
                    <Switch
                      checked={row.show_in_mobile !== false}
                      onCheckedChange={() => toggleChannel(row, "show_in_mobile")}
                      data-testid={`ms-cat-mobile-${row.id}`}
                    />
                  </div>
                  <div className="w-20 flex justify-center" title={row.show_in_pos !== false ? "Available in POS — click to hide" : "Hidden from POS — click to show"}>
                    <Switch
                      checked={row.show_in_pos !== false}
                      onCheckedChange={() => toggleChannel(row, "show_in_pos")}
                      data-testid={`ms-cat-pos-${row.id}`}
                    />
                  </div>
                  <div className="w-24 flex justify-end gap-1">
                    <Button size="sm" variant="outline" onClick={() => startEdit(row)}
                      className="h-8 px-2" title="Rename" data-testid={`ms-cat-rename-${row.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="outline"
                      onClick={() => removeCat(row)}
                      className="h-8 px-2 text-red-600 hover:bg-red-50 hover:border-red-300"
                      title="Delete" data-testid={`ms-cat-delete-${row.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="ms-manage-cats-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
